import React, { useEffect, useState, useMemo } from "react"
import { showSnackBar } from "../Snackbar/Snackbar"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import CreditReportModal from "../CreditReportModal/CreditReportModal"
import { selectFeatureFlag } from "../../features/featureFlags/selectors"
import { FLAT_FEE_TYPED_AMOUNT, COAPP_CREDIT_REFETCH_TAB, PORTAL_CREDIT_SCORES, PROGRAM_FEE_FLOOR_15, DEBT_WORK_STATUS } from "../../config/featureFlagKeys"
import { getContactCreditScores, setPortalCreditScoresShown } from "../../features/creditScores/creditScores"
import { storedRole } from "../../features/notifications/audience"
import ContactCreditScores from "../CreditScores/ContactCreditScores"
import {
  getDebtsByUser,
  getPaymentScheduleByUser, getPaymentScheduleStatisticsByUser,
  refetchCreditReport,
  getCreditReportsByUserId,
  getMergedCreditProfiles,
  getHouseholdCoverage,
} from "../../features/creditReports/creditReports"
import { getContactLinksByUserId } from "../../features/contactLinks/contactLinks"
import CreditReportsTable from "./CreditReportsTable"
import Button from "../Button/Button"
import Spinner from "../Spiner/Spiner"
import PaymentScheduleTable from "./PaymentScheduleTable"
import SubStatistics from "./SubStatistics"
import CreditReportsHistoryTable from "./CreditReportsHistoryTable"
import PdfViewerDrawer from "./PdfViewerDrawer"
import DebtWorkStatusDrawer from "./DebtWorkStatusDrawer"
import { canEditDebtWorkStatus } from "./debtWorkStatus"
import useViewPdf from '../../hooks/useViewPdf'
import PdfBureauButton from './PdfBureauButton'
import AddCoApplicantModal from "../AddCoApplicantModal/AddCoApplicantModal"
import { describeRefetchOutcome } from "./refetchOutcome"
import HouseholdCoverage from "./HouseholdCoverage"
import { StorageGetItem, USER } from "../../lib/storage"

const withWorkStatus = (rows, debtId, workStatus) =>
  rows.map(row => (row._id === debtId ? { ...row, workStatus } : row))

const CreditReports = () => {
  const dispatch = useDispatch()
  // Flat fee typed by hand in the regenerate-plan modal rather than picked (WS #1944843).
  const flatFeeTyped = useSelector(selectFeatureFlag(FLAT_FEE_TYPED_AMOUNT))
  const lowerFeeFloor = useSelector(selectFeatureFlag(PROGRAM_FEE_FLOOR_15))
  // Lets staff pull the co-applicant's credit from this tab and see their reports in the history
  // (WS #2115295). Off: the one Re-fetch button pulls the contact on the page, as before.
  const coAppRefetch = useSelector(selectFeatureFlag(COAPP_CREDIT_REFETCH_TAB))
  const creditScoresOn = useSelector(selectFeatureFlag(PORTAL_CREDIT_SCORES))
  const workStatusOn = useSelector(selectFeatureFlag(DEBT_WORK_STATUS))
  const canEditWorkStatus = canEditDebtWorkStatus(storedRole())
  const canEditScores = ['customer', 'supervisor', 'user'].includes(storedRole())
  const [scores, setScores] = useState(null)
  const [savingScores, setSavingScores] = useState(false)
  const [scoresReload, setScoresReload] = useState(0)
  const { contactId: userId } = useParams()
  const [reqError, setReqError] = useState('')
  const [loading, setLoading] = useState(true)
  const [isUpdate, setIsUpdate] = useState(false)
  const [data, setData] = useState([])
  const [dataHistory, setDataHistory] = useState([])
  const [dataPaymentsSchedule, setDataPaymentsSchedule] = useState([])
  const [dataPaymentsScheduleStatistics, setDataPaymentsScheduleStatistics] = useState([])
  const { pdfUrl, pdfOpen, pdfLoading, loadedReportId, openPdf, closePdf, resetPdf } = useViewPdf()
  // The id being pulled, or null.
  const [refetching, setRefetching] = useState(null)
  const [coverageSubjects, setCoverageSubjects] = useState([])
  const [coverageError, setCoverageError] = useState('')
  const [hasCoApplicant, setHasCoApplicant] = useState(false)
  const [coApplicants, setCoApplicants] = useState([])
  const [coApplicantHistory, setCoApplicantHistory] = useState([])
  const [viewMode, setViewMode] = useState('all')
  const [mergedData, setMergedData] = useState([])
  const [showAddCoApplicant, setShowAddCoApplicant] = useState(false)
  const [userCompany, setUserCompany] = useState('')
  const [workStatusDebt, setWorkStatusDebt] = useState(null)

  const handleWorkStatusSaved = (debtId, workStatus) => {
    setData(rows => withWorkStatus(rows, debtId, workStatus))
    setMergedData(rows => withWorkStatus(rows, debtId, workStatus))
    setWorkStatusDebt(debt => (debt && debt._id === debtId ? { ...debt, workStatus } : debt))
  }

  // Build bureau → reportId map from enrolled debts
  const reportMap = useMemo(() => {
    const map = {}
    for (const item of data || []) {
      const bureau = item.sourceType
      const ref = item.creditReport
      const refId = typeof ref === 'object' ? ref?._id : ref
      if (bureau && refId && !map[bureau]) map[bureau] = refId
    }
    return map
  }, [data])


  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserCompany(result?.company || '')
    })
  }, [])

  useEffect(() => {
    const handleReqError = () => {
      reqError?.length > 0 && showSnackBar("error", reqError)
      setReqError('')
    }

    handleReqError()
  }, [reqError])

  const getDebts = async () => {
    try {
      const response = await dispatch(getDebtsByUser(userId))
      if (response.payload && response.payload.data) {
        setData(response?.payload?.data.map((item, index) => ({ ...item, id: index })))
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error fetching credit reports:', error)
    }
  }

  const getPaymentSchedule = async () => {
    try {
      const response = await dispatch(getPaymentScheduleByUser(userId))
      if (response.payload && response.payload.data) {
        setDataPaymentsSchedule(response?.payload?.data.map((item, index) => ({ ...item, id: index })))
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error fetching credit reports:', error)
    }
  }

  const getHistory = async () => {
    try {
      const response = await dispatch(getCreditReportsByUserId({ userId }))
      if (response.payload && response.payload.data) {
        setDataHistory(response.payload.data)
      }
    } catch (error) {
      setReqError('Failed to load credit report history')
    }
  }

  const checkForCoApplicant = async () => {
    try {
      const response = await dispatch(getContactLinksByUserId(userId))
      const links = Array.isArray(response?.payload) ? response.payload : []
      // Only the co-applicants of the contact on the page: the server's merged view and household
      // coverage follow primary -> co-applicant only.
      setCoApplicants(links
        .filter(link => link.linkType === 'co-applicant' && link.primaryContact?._id === userId && link.linkedContact?._id)
        .map(link => link.linkedContact))
      if (links.length > 0) {
        setHasCoApplicant(true)
        const mergedResponse = await dispatch(getMergedCreditProfiles(userId))
        if (mergedResponse?.payload) {
          setMergedData(mergedResponse.payload.map((item, index) => ({ ...item, id: index })))
        }
      }
    } catch (error) {
      console.error('Failed to check co-applicant')
    }
  }

  const getPaymentScheduleStatistics = async () => {
    try {
      const response = await dispatch(getPaymentScheduleStatisticsByUser(userId))
      if (response.payload && response.payload.data) {
        setDataPaymentsScheduleStatistics(response?.payload?.data)
      } else {
        console.error('No data received from server')
      }
    } catch (error) {
      console.error('Error fetching credit reports:', error)
    }
  }

  useEffect(() => {
    if (!coAppRefetch || coApplicants.length === 0) {
      setCoApplicantHistory([])
      return
    }
    const loadCoApplicantHistory = async () => {
      const results = await Promise.all(coApplicants.map(person =>
        dispatch(getCreditReportsByUserId({ userId: person._id }))))
      if (results.some(result => getCreditReportsByUserId.rejected.match(result))) {
        setReqError('Failed to load the co-applicant credit report history')
      }
      setCoApplicantHistory(results.flatMap(result => Array.isArray(result.payload?.data)
        ? result.payload.data.map(row => ({ ...row, source: 'co-applicant' }))
        : []))
    }
    loadCoApplicantHistory()
  }, [coAppRefetch, coApplicants, dispatch])

  const historyRows = useMemo(() => {
    if (coApplicantHistory.length === 0) return dataHistory
    return [...dataHistory.map(row => ({ ...row, source: 'applicant' })), ...coApplicantHistory]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
  }, [dataHistory, coApplicantHistory])

  const loadCoverage = async () => {
    const res = await dispatch(getHouseholdCoverage(userId))
    if (getHouseholdCoverage.rejected.match(res)) {
      setCoverageSubjects([])
      setCoverageError(res.payload || 'Failed to read household credit coverage')
      return
    }
    setCoverageError('')
    setCoverageSubjects(res.payload?.subjects || [])
  }

  const handleRefetch = async (subjectId) => {
    setRefetching(subjectId)
    try {
      const result = await dispatch(refetchCreditReport(subjectId))
      if (result?.error) {
        showSnackBar('error', result?.payload || 'Failed to re-fetch credit report')
      } else {
        const outcome = describeRefetchOutcome(result?.payload?.data)
        showSnackBar(outcome.severity, outcome.message)
        resetPdf()
        setScoresReload((count) => count + 1)
        await loadData()
        await loadCoverage()
      }
    } catch {
      showSnackBar('error', 'Failed to re-fetch credit report')
    } finally {
      setRefetching(null)
    }
  }

  useEffect(() => {
    if (!creditScoresOn) {
      setScores(null)
      return undefined
    }
    let cancelled = false
    const loadScores = async () => {
      const result = await dispatch(getContactCreditScores(userId))
      if (cancelled) return
      setScores(result?.error || !result?.payload ? null : result.payload)
    }
    loadScores()
    return () => {
      cancelled = true
    }
  }, [creditScoresOn, userId, isUpdate, scoresReload, dispatch])

  const handleToggleScores = async (next) => {
    setSavingScores(true)
    try {
      const result = await dispatch(setPortalCreditScoresShown({ userId, shown: next }))
      if (result?.error || !result?.payload) {
        showSnackBar('error', result?.payload?.message || 'Failed to update the client portal setting')
      } else {
        setScores((current) => (current ? { ...current, shownInPortal: result.payload.shownInPortal } : current))
      }
    } finally {
      setSavingScores(false)
    }
  }

  const loadData = async () => {
    setLoading(true)
    setData([])
    setDataHistory([])
    setDataPaymentsSchedule([])
    setDataPaymentsScheduleStatistics([])
    await Promise.all([
      getHistory(),
      getPaymentSchedule(),
      getDebts(),
      getPaymentScheduleStatistics(),
      checkForCoApplicant(),
    ])
    setLoading(false)
  }

  useEffect(() => {
    loadData()
    loadCoverage()
  }, [isUpdate])

  const renderRefetchButton = ({ subjectId, label, title }) => (
    <button
      key={subjectId}
      disabled={!!refetching}
      onClick={() => handleRefetch(subjectId)}
      title={title}
      className="px-4 py-2 text-sm font-medium border border-divider rounded-lg hover:bg-surface transition-colors disabled:opacity-50"
    >
      {refetching === subjectId ? 'Fetching...' : label}
    </button>
  )

  return (
    <div className="flex flex-col w-full">
      <div className="flex justify-between w-full mb-8">
        <div className="mx-5 flex items-center">
          <span className=" text-xl-20 font-bold">Enrolled Debts</span>
        </div>
        {/*<CreditReportsBalance dashboardData={}/>*/}
      </div>
      <div className="mx-5 ">
        <div className="flex justify-between items-center border-b border-divider pb-6">
          <div className="mx-5 my-4">
            <h5 className=" text-xl-20 font-bold">Debts</h5>
            <SubStatistics statisticsData={dataPaymentsScheduleStatistics} />
          </div>
          <div className="flex items-center mr-5 gap-3">
            <PdfBureauButton reportMap={reportMap} openPdf={openPdf} pdfLoading={pdfLoading} />
            {!hasCoApplicant && (
              <button
                onClick={() => setShowAddCoApplicant(true)}
                className="px-4 py-2 text-sm font-medium border border-divider rounded-lg hover:bg-surface transition-colors"
              >
                Add Co-Applicant
              </button>
            )}
            {coAppRefetch && coApplicants.length > 0 ? (
              <>
                {renderRefetchButton({ subjectId: userId, label: 'Re-fetch Applicant' })}
                {coApplicants.map(person => renderRefetchButton({
                  subjectId: person._id,
                  label: coApplicants.length > 1 ? `Re-fetch Co-Applicant (${person.firstName || ''})` : 'Re-fetch Co-Applicant',
                  title: `Pull ${[person.firstName, person.lastName].filter(Boolean).join(' ')}'s credit report`,
                }))}
              </>
            ) : renderRefetchButton({ subjectId: userId, label: 'Re-fetch Credit Report' })}
            <CreditReportModal userId={userId} setIsUpdate={setIsUpdate} isUpdate={isUpdate} debts={data}
              dataPaymentsSchedule={dataPaymentsSchedule} flatFeeTyped={flatFeeTyped} lowerFeeFloor={lowerFeeFloor} />
          </div>
        </div>
        {hasCoApplicant && (
          <div className="flex gap-2 my-4">
            <Button
              size="sm"
              type={viewMode === 'all' ? "contained" : "outlined"}
              onClick={() => setViewMode('all')}
            >
              All
            </Button>
            <Button
              size="sm"
              type={viewMode === 'applicant' ? "contained" : "outlined"}
              onClick={() => setViewMode('applicant')}
            >
              Applicant
            </Button>
            <Button
              size="sm"
              type={viewMode === 'co-applicant' ? "contained" : "outlined"}
              onClick={() => setViewMode('co-applicant')}
            >
              Co-Applicant
            </Button>
          </div>
        )}
        <HouseholdCoverage subjects={coverageSubjects} error={coverageError} />
        <CreditReportsTable data={
          !hasCoApplicant || mergedData.length === 0 ? data :
          viewMode === 'all' ? mergedData :
          viewMode === 'applicant' ? mergedData.filter(d => d.source === 'applicant' || d.source === 'both') :
          mergedData.filter(d => d.source === 'co-applicant' || d.source === 'both')
        } {...(workStatusOn ? { workStatusOn, canEditWorkStatus, onOpenWorkStatus: setWorkStatusDebt } : {})} />
      </div>
      {scores && scores.reportDayCount > 0 && (
        <div className="mx-5 mt-8">
          <ContactCreditScores
            data={scores}
            canEdit={canEditScores}
            onToggle={handleToggleScores}
            saving={savingScores}
          />
        </div>
      )}
      <div className="mx-5 mt-8">
        <div className="flex justify-between w-full pt-4 border-b border-divider">
          <div className="mx-5 mb-4">
            <h5 className="text-xl-20 font-bold">Credit Report History</h5>
          </div>
        </div>
        <CreditReportsHistoryTable data={historyRows} onViewPdf={openPdf} pdfLoading={pdfLoading} loadingReportId={loadedReportId} />
      </div>
      <div className="mx-5 mt-8">
        <div className="flex justify-between w-full pt-4 border-b border-divider">
          <div className="mx-5 mb-4">
            <h5 className=" text-xl-20 font-bold">Payment Schedule</h5>
            <SubStatistics statisticsData={dataPaymentsScheduleStatistics} />
          </div>
          {/*<CreditReportsBalance dashboardData={}/>*/}
        </div>
        <PaymentScheduleTable data={dataPaymentsSchedule} />
      </div>
      {loading && <Spinner />}

      <AddCoApplicantModal
        open={showAddCoApplicant}
        onClose={() => setShowAddCoApplicant(false)}
        userId={userId}
        company={userCompany}
        onSuccess={() => loadData()}
      />

      <PdfViewerDrawer
        open={pdfOpen}
        pdfUrl={pdfUrl}
        reportId={loadedReportId}
        onClose={closePdf}
      />

      {workStatusOn && (
        <DebtWorkStatusDrawer
          open={Boolean(workStatusDebt)}
          debt={workStatusDebt}
          canEdit={canEditWorkStatus}
          onClose={() => setWorkStatusDebt(null)}
          onSaved={handleWorkStatusSaved}
        />
      )}
    </div>
  )
}
export default CreditReports
